import AddIcon from '@mui/icons-material/Add';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { useSnackbar } from 'notistack';
import { type ReactNode, useRef, useState } from 'react';

import { PillRadioGroup } from '~/components/common/PillRadioGroup';
import { LiftAITable } from '~/components/table';
import type { LiftAIGridColDef } from '~/components/table/colDefTemplate';
import { useTasksEnabled } from '~/components/tasks/useTasksEnabled';
import type { KnowledgeEntry, KnowledgeEntryKindEnum } from '~/data/generated/model';
import { propertiesKnowledgePartialUpdateBodyContentMax } from '~/data/generated/zod/properties/properties.zod';
import {
  useCarKnowledge,
  useDeleteKnowledge,
  usePropertyKnowledge,
  useRestoreKnowledge,
  useUpdateKnowledge,
} from '~/data/hooks/useKnowledge';
import { useKnowledgeTargets } from '~/data/hooks/useKnowledgeTargets';
import { BUTTON_SX, SURFACE_RADIUS } from '~/theme';

import { AddKnowledgeDialog, PROPERTY_WIDE_LABEL } from './AddKnowledgeDialog';
import {
  formatKnowledgeDate,
  KNOWLEDGE_KIND_LABELS,
  KNOWLEDGE_KIND_OPTIONS,
  KnowledgeKindChip,
  KnowledgeSourceBadge,
} from './KnowledgeBits';
import { useKnowledgeEnabled } from './useKnowledgeEnabled';

// An entry outlives its elevator: what was learned about a replaced car still matters.
const REMOVED_CAR_LABEL = 'Removed elevator';

const SUPERSEDED_ROW = 'knowledge-row--superseded';

const CENTERED_CELL = { display: 'flex', alignItems: 'center', height: '100%' } as const;

const authorName = (entry: KnowledgeEntry) => entry.author?.name || 'Former user';

function EntryActions({
  entry,
  canRestore,
  onEdit,
  onReplace,
  onDelete,
  onRestore,
}: {
  entry: KnowledgeEntry;
  /** Only whoever made the replacement can undo it. */
  canRestore: boolean;
  onEdit: () => void;
  onReplace: () => void;
  onDelete: () => void;
  onRestore: () => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const superseded = Boolean(entry.supersededById);
  const pick = (action: () => void) => () => {
    setAnchor(null);
    action();
  };
  if (superseded && !canRestore && !entry.isAuthor) return null;
  return (
    <>
      <IconButton
        size="small"
        aria-label="Entry actions"
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
      >
        <MoreVertRoundedIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {/* Only the author can edit or delete an entry. */}
        {entry.isAuthor ? <MenuItem onClick={pick(onEdit)}>Edit</MenuItem> : null}
        {superseded ? (
          canRestore ? (
            <MenuItem onClick={pick(onRestore)}>Undo supersede</MenuItem>
          ) : null
        ) : (
          <MenuItem onClick={pick(onReplace)}>Supersede…</MenuItem>
        )}
        {entry.isAuthor ? (
          <MenuItem onClick={pick(onDelete)} sx={{ color: 'error.main' }}>
            Delete
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
}

function EditKnowledgeDialog({ entry, onClose }: { entry: KnowledgeEntry; onClose: () => void }) {
  const updateKnowledge = useUpdateKnowledge();
  const [kind, setKind] = useState<KnowledgeEntryKindEnum>(entry.kind);
  const [content, setContent] = useState(entry.content);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (busyRef.current || !content.trim()) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await updateKnowledge(entry, { kind, content: content.trim() });
      onClose();
    } catch {
      setError('This could not be saved. Try again.');
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="edit-knowledge-title">
      <DialogTitle id="edit-knowledge-title">Edit knowledge</DialogTitle>
      <DialogContent>
        <Stack spacing="14px" sx={{ pt: 1 }}>
          <PillRadioGroup
            label="Type"
            options={KNOWLEDGE_KIND_OPTIONS}
            value={kind}
            onChange={setKind}
          />
          <TextField
            multiline
            minRows={3}
            size="small"
            label="Entry text"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            slotProps={{ htmlInput: { maxLength: propertiesKnowledgePartialUpdateBodyContentMax } }}
          />
          {error ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} disabled={busy} sx={BUTTON_SX}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={busy || !content.trim()}
          onClick={() => void save()}
          sx={BUTTON_SX}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeleteKnowledgeDialog({ entry, onClose }: { entry: KnowledgeEntry; onClose: () => void }) {
  const deleteKnowledge = useDeleteKnowledge();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remove = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await deleteKnowledge(entry);
      onClose();
    } catch {
      setError('This could not be deleted. Try again.');
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} aria-labelledby="delete-knowledge-title">
      <DialogTitle id="delete-knowledge-title">Delete this entry?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {entry.content}
        </Typography>
        {error ? (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} disabled={busy} sx={BUTTON_SX}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={busy}
          onClick={() => void remove()}
          sx={BUTTON_SX}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ReplaceHistory({ entry, all }: { entry: KnowledgeEntry; all: KnowledgeEntry[] }) {
  const newer = entry.supersededById && all.find((e) => e.id === entry.supersededById);
  const older = entry.supersedesId && all.find((e) => e.id === entry.supersedesId);
  if (!newer && !older) return null;
  return (
    <Stack spacing={0.5} sx={{ fontSize: 12, color: '#555', mt: '4px' }}>
      {older ? (
        <Box sx={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
          <SwapHorizIcon sx={{ fontSize: 14, mt: '2px' }} />
          <span>
            Supersedes “{older.content}” · {formatKnowledgeDate(older.createdAt)}
          </span>
        </Box>
      ) : null}
      {newer ? (
        <Box sx={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
          <SwapHorizIcon sx={{ fontSize: 14, mt: '2px' }} />
          <span>
            Superseded by {authorName(newer)} · {formatKnowledgeDate(newer.createdAt)}: “
            {newer.content}”
          </span>
        </Box>
      ) : null}
    </Stack>
  );
}

interface KnowledgeListProps {
  propertyId: string;
  entries: KnowledgeEntry[];
  isLoading: boolean;
  error?: unknown;
  carId?: string;
  description?: string;
  headerActions?: ReactNode;
  toolbarExtra?: ReactNode;
  emptyMessage?: string;
}

export function KnowledgeList({
  propertyId,
  entries,
  isLoading,
  error,
  carId,
  description = 'Notes, decisions and vendor commitments the team has recorded.',
  headerActions,
  toolbarExtra,
  emptyMessage = 'No knowledge recorded here yet.',
}: KnowledgeListProps) {
  const enabled = useKnowledgeEnabled();
  const tasksEnabled = useTasksEnabled();
  const restoreKnowledge = useRestoreKnowledge();
  const { enqueueSnackbar } = useSnackbar();
  const { properties } = useKnowledgeTargets();
  const apiRef = useGridApiRef();
  const [showSuperseded, setShowSuperseded] = useState(false);
  const [adding, setAdding] = useState<{ replaces?: KnowledgeEntry } | null>(null);
  const [editing, setEditing] = useState<KnowledgeEntry | null>(null);
  const [deleting, setDeleting] = useState<KnowledgeEntry | null>(null);

  const property = properties.find((p) => p.id === propertyId);
  const cars = property?.cars ?? [];
  const carNames = new Map(cars.map((c) => [c.id, c.name]));

  const supersededCount = entries.filter((e) => e.supersededById).length;
  const visible = entries.filter((e) => showSuperseded || !e.supersededById);

  const restore = async (entry: KnowledgeEntry) => {
    try {
      await restoreKnowledge(entry);
      enqueueSnackbar('The older entry is current again', { variant: 'success' });
    } catch {
      enqueueSnackbar('This could not be undone. Try again.', { variant: 'error' });
    }
  };

  const columns = ((): LiftAIGridColDef<KnowledgeEntry>[] => {
    const elevatorName = (entry: KnowledgeEntry) =>
      entry.carId
        ? (carNames.get(entry.carId) ?? (property ? REMOVED_CAR_LABEL : ''))
        : PROPERTY_WIDE_LABEL;
    const cols: LiftAIGridColDef<KnowledgeEntry>[] = [
      {
        field: 'content',
        headerName: 'Knowledge',
        flex: 3,
        minWidth: 320,
        sortable: false,
        renderCell: ({ row }) => (
          <Box sx={{ py: '10px', whiteSpace: 'normal', lineHeight: 1.45 }}>
            <Box sx={{ whiteSpace: 'pre-wrap', textWrap: 'pretty' }}>{row.content}</Box>
            {row.supersededById ? (
              <Box component="span" sx={{ fontSize: 11, color: '#46505c', fontWeight: 500 }}>
                Superseded
              </Box>
            ) : null}
            <ReplaceHistory entry={row} all={entries} />
          </Box>
        ),
      },
      {
        field: 'kind',
        headerName: 'Type',
        width: 130,
        valueFormatter: (value: KnowledgeEntryKindEnum) => KNOWLEDGE_KIND_LABELS[value],
        renderCell: ({ row }) => (
          <Box sx={CENTERED_CELL}>
            <KnowledgeKindChip kind={row.kind} />
          </Box>
        ),
      },
    ];
    if (!carId) {
      cols.push({
        field: 'carId',
        headerName: 'Elevator',
        width: 150,
        valueGetter: (_value, row) => elevatorName(row),
      });
    }
    cols.push(
      {
        field: 'author',
        headerName: 'Author',
        width: 150,
        valueGetter: (_value, row) => authorName(row),
      },
      {
        field: 'createdAt',
        headerName: 'Date',
        width: 120,
        valueFormatter: (value: string) => formatKnowledgeDate(value),
      },
      {
        field: 'source',
        headerName: 'Source',
        width: 170,
        sortable: false,
        renderCell: ({ row }) => (
          <Box sx={CENTERED_CELL}>
            <KnowledgeSourceBadge source={row.source} task={row.task} linkTask={tasksEnabled} />
          </Box>
        ),
      },
    );
    if (enabled) {
      cols.push({
        field: 'actions',
        headerName: '',
        width: 56,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <EntryActions
            entry={row}
            canRestore={Boolean(entries.find((e) => e.id === row.supersededById)?.isAuthor)}
            onEdit={() => setEditing(row)}
            onReplace={() => setAdding({ replaces: row })}
            onDelete={() => setDeleting(row)}
            onRestore={() => void restore(row)}
          />
        ),
      });
    }
    return cols;
  })();

  const supersededToggle =
    supersededCount > 0 ? (
      <FormControlLabel
        sx={{ m: 0, color: 'text.secondary', whiteSpace: 'nowrap' }}
        slotProps={{ typography: { fontSize: 13 } }}
        control={
          <Checkbox
            size="small"
            checked={showSuperseded}
            onChange={(e) => setShowSuperseded(e.target.checked)}
          />
        }
        label={`Show superseded · ${supersededCount}`}
      />
    ) : null;

  return (
    <Box
      component="section"
      sx={{ display: 'flex', flexDirection: 'column', gap: '14px', p: '0 8px' }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
        <Box sx={{ flex: 1, minWidth: 240, fontSize: 14, color: '#555' }}>{description}</Box>
        {headerActions}
        {enabled ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAdding({})}
            sx={BUTTON_SX}
          >
            Add knowledge
          </Button>
        ) : null}
      </Box>

      {error ? <Alert severity="error">Knowledge could not be loaded. Try again.</Alert> : null}

      {isLoading && entries.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : !error && entries.length === 0 && !toolbarExtra ? (
        <Box
          sx={{
            p: '40px',
            textAlign: 'center',
            color: '#777',
            fontSize: 13,
            border: '1px dashed #d7dbe0',
            borderRadius: `${SURFACE_RADIUS}px`,
          }}
        >
          {emptyMessage}
        </Box>
      ) : error ? null : (
        <Box sx={{ [`& .${SUPERSEDED_ROW}`]: { opacity: 0.62, bgcolor: '#fafbfc' } }}>
          <LiftAITable<KnowledgeEntry>
            apiRef={apiRef}
            rows={visible}
            columns={columns}
            tableHeaderTitle="Knowledge"
            toolbarProps={{
              beforeSearch:
                toolbarExtra || supersededToggle ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {toolbarExtra}
                    {supersededToggle}
                  </Box>
                ) : null,
            }}
            enableQuickFilter
            quickFilterPlaceHolder="Search knowledge…"
            getRowHeight={() => 'auto'}
            getRowClassName={({ row }) => (row.supersededById ? SUPERSEDED_ROW : '')}
            initialState={{ sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] } }}
            paginationModel={{ page: 0, pageSize: 25 }}
          />
        </Box>
      )}

      {adding ? (
        <AddKnowledgeDialog
          initialTarget={{ propertyId, ...(carId ? { carId } : {}) }}
          initialReplaces={adding.replaces}
          lockCar={Boolean(carId)}
          onClose={() => setAdding(null)}
        />
      ) : null}
      {editing ? <EditKnowledgeDialog entry={editing} onClose={() => setEditing(null)} /> : null}
      {deleting ? (
        <DeleteKnowledgeDialog entry={deleting} onClose={() => setDeleting(null)} />
      ) : null}
    </Box>
  );
}

export function PropertyKnowledgeTab({ propertyId }: { propertyId: string }) {
  const { entries, isLoading, error } = usePropertyKnowledge(propertyId);
  return (
    <KnowledgeList propertyId={propertyId} entries={entries} isLoading={isLoading} error={error} />
  );
}

export function CarKnowledgeTab({ carId, propertyId }: { carId: string; propertyId: string }) {
  const { entries, isLoading, error } = useCarKnowledge(carId);
  return (
    <KnowledgeList
      propertyId={propertyId}
      entries={entries}
      isLoading={isLoading}
      error={error}
      carId={carId}
    />
  );
}
